import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert } from 'react-bootstrap';
import ExecutionTable from '../components/common/ExecutionTable';
import MessageResultsTable from '../components/common/MessageResultsTable';
import { useExecution } from '../contexts/ExecutionContext';
import api from '../services/api';

const MessageClassificationPage = () => {
  // Get execution context
  const {
    isModuleRunning,
    getModuleProgress,
    getRunningExecutionId,
    startExecution,
    isAnyModuleRunning,
    getRunningModuleName,
    executionCompletionTrigger,
    executionStartTrigger
  } = useExecution();
  
  // States for execution data
  const [executions, setExecutions] = useState([]);
  const [statistics, setStatistics] = useState(null);
  
  // Pagination state for executions
  const [executionPage, setExecutionPage] = useState(1);
  const [executionTotalPages, setExecutionTotalPages] = useState(1);
  const [executionSortOrder, setExecutionSortOrder] = useState("desc");
  
  // States for running module
  const [selection, setSelection] = useState('all');
  
  // States for results table
  const [messageResults, setMessageResults] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sort, setSort] = useState('message_id');
  const [sortOrder, setSortOrder] = useState('asc');
  const [filter, setFilter] = useState({});
  
  // State for error messages
  const [error, setError] = useState(null);

  // Module-specific state derived from centralized execution context
  const moduleName = "message_classification";
  const isRunning = isModuleRunning(moduleName);
  const runProgress = getModuleProgress(moduleName);
  const currentExecutionId = getRunningExecutionId(moduleName);
  
  // Fetch executions
  const fetchExecutions = useCallback(async () => {
    try {
      const response = await api.getExecutions(
        "message_classification",
        "timestamp_start",
        executionSortOrder,
        executionPage,
        10
      );
      setExecutions(response.data?.executions || []);
      setExecutionTotalPages(response.data?.nr_of_pages || 1);
    } catch (err) {
      console.error('Error fetching executions:', err);
      setError('Failed to load execution history.');
    }
  }, [executionPage, executionSortOrder]);

  // Fetch statistics
  const fetchStatistics = useCallback(async () => {
    try {
      const response = await api.getBasicStatistics();
      setStatistics(response.data);
    } catch (err) {
      console.error('Error fetching statistics:', err);
      setError('Failed to load statistics.');
    }
  }, []);

  // Fetch message results
  const fetchMessageResults = useCallback(async () => {
    try {

      const params = {
        page: currentPage,
        page_size: pageSize,
        sort: sort,
        sort_order: sortOrder,
      };

      const requestBody = filter;
      
      const response = await api.getClassificationResults(params, requestBody);
      setMessageResults(response.data.messages || []);
      setTotalPages(response.data.nr_of_pages || 0);
      // Clear any previous error since we successfully fetched data (even if it's empty)
      setError(null);
    } catch (err) {
      console.error('Error fetching message results:', err);
      // Only set error for actual API errors, not empty data
      if (err.response && err.response.status !== 404) {
        setError('Failed to load message results.');
      }
    }
  }, [currentPage, pageSize, sort, sortOrder, filter]);

  // Run message classification
  const runMessageClassification = async () => {
    try {
      // Check if any module is already running
      if (isAnyModuleRunning()) {
        const runningModule = getRunningModuleName();
        setError(`Cannot start message classification. ${runningModule} is currently running.`);
        return;
      }

      const response = await api.runMessageClassifier(selection, true); // Pass run_in_background=true
      
      if (response.data && response.data.execution_id) {
        if (response.data.error_flag) {
          throw new Error(`Failed to start message classification process: ${response.data.error_message || 'Unknown error'}`);
        }
        // Start tracking this execution in the centralized state
        startExecution(moduleName, response.data.execution_id);
      } else {
        throw new Error('No execution ID returned');
      }
    } catch (err) {
      console.error('Error running message classification:', err);
      setError('Failed to start message classification process.');
    }
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle sort change
  const handleSortChange = (field, order) => {
    setSort(field);
    setSortOrder(order);
  };

  // Handle filter change
  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Handle page size change
  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when page size changes
  };

  // Fetch initial data
  useEffect(() => {
    fetchExecutions();
    fetchStatistics();
    fetchMessageResults();
  }, [fetchExecutions, fetchStatistics, fetchMessageResults]);

  // Refresh data when any execution completes
  useEffect(() => {
    if (executionCompletionTrigger > 0) {
      // An execution has completed, refresh data
      fetchExecutions();
      fetchStatistics();
      fetchMessageResults();
    }
  }, [executionCompletionTrigger, fetchExecutions, fetchStatistics, fetchMessageResults]);

  // Refresh execution history when any execution starts
  useEffect(() => {
    if (executionStartTrigger > 0) {
      // An execution has started, refresh execution history to show the new running entry
      fetchExecutions();
    }
  }, [executionStartTrigger, fetchExecutions]);

  // Fetch results when page, size, sort, or filter changes
  useEffect(() => {
    fetchMessageResults();
  }, [fetchMessageResults]);

  return (
    <Container className="py-4">
      <h1 className="mb-4">Message Classification</h1>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Header>Message Classification Statistics</Card.Header>
            <Card.Body>
              {statistics ? (
                <div>
                  <div className="d-flex justify-content-between mb-2">
                    <span><b>Messages Ready To Process:</b></span>
                    <span className="fw-bold">{statistics.nr_messages_with_activity_unprocessed_ready || 0}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Messages Processed:</span>
                    <span className="fw-bold">{statistics.nr_messages_with_activity_processed || 0}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Activities identified:</span>
                    <span className="fw-bold">{statistics.nr_messages_with_valid_activity || 0}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Messages Missing Case Attribution Step:</span>
                    <span className="fw-bold">{(statistics.nr_messages_case_id_unprocessed) || 0}</span>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6}>
          <Card>
            <Card.Header>Run Message Classification</Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Data Selection Mode</Form.Label>
                <Form.Select 
                  value={selection} 
                  onChange={(e) => setSelection(e.target.value)}
                  disabled={isRunning}
                >
                  <option value="all">Reprocess ALL</option>
                  <option value="new">Process NEW</option>
                  <option value="open">OPEN</option>
                </Form.Select>
              </Form.Group>
              
              <Button 
                variant="primary" 
                onClick={runMessageClassification}
                disabled={isRunning}
                className="w-100"
              >
                {isRunning ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Running... ({runProgress}%)
                  </>
                ) : (
                  'Run Message Classification'
                )}
              </Button>
              
              {isRunning && (
                <div className="mt-3">
                  <div className="progress">
                    <div 
                      className="progress-bar" 
                      role="progressbar" 
                      style={{ width: `${runProgress}%` }}
                      aria-valuenow={runProgress} 
                      aria-valuemin="0" 
                      aria-valuemax="100"
                    >
                      {runProgress}%
                    </div>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      <Row className="mb-4">
        <Col>
          <h3>Execution History</h3>
          <ExecutionTable
            executions={executions}
            title=""
            currentPage={executionPage}
            totalPages={executionTotalPages}
            pageSize={10}
            sortOrder={executionSortOrder}
            onPageChange={(page) => setExecutionPage(page)}
            onSortChange={(sortOrder) => setExecutionSortOrder(sortOrder)}
          />
        </Col>
      </Row>
      
      <Row>
        <Col>
          <h3>Last Execution Results</h3>
          <MessageResultsTable 
            messages={messageResults}
            totalPages={totalPages}
            currentPage={currentPage}
            pageSize={pageSize}
            sort={sort}
            sortOrder={sortOrder}
            filter={filter}
            onPageChange={handlePageChange}
            onSortChange={handleSortChange}
            onFilterChange={handleFilterChange}
            onPageSizeChange={handlePageSizeChange}
            mode="message_classification"
          />
        </Col>
      </Row>
    </Container>
  );
};

export default MessageClassificationPage;